import { ApiError } from "./api";
import type { BootstrapResponse, WorkMutationResult, WorkMutationSnapshot } from "./types";

export type WorkDataPatch = (data: BootstrapResponse) => BootstrapResponse;
export interface WorkMutation<T extends WorkMutationResult> {
  key: string;
  label: string;
  entityIds: string[];
  optimistic?: WorkDataPatch;
  request: () => Promise<T>;
  onSuccess?: (result: T) => void;
}
export interface PendingWorkMutation {
  key: string;
  label: string;
  entityIds: string[];
  status: "saving" | "uncertain";
}

export const mergeWorkSnapshot = (data: BootstrapResponse, snapshot: WorkMutationSnapshot): BootstrapResponse => {
  const replaceProjectRows = <T extends { projectId: string }>(rows: T[], values: T[]) =>
    [...rows.filter(row => row.projectId !== snapshot.projectId), ...values];
  const oldReturnIds = new Set(data.issuances.filter(row => row.projectId === snapshot.projectId).flatMap(row => row.returns.map(value => value.id)));
  return { ...data,
    ...(snapshot.projectCompleted !== undefined ? { completedProjectIds: snapshot.projectCompleted
      ? [...new Set([...(data.completedProjectIds ?? []), snapshot.projectId])]
      : (data.completedProjectIds ?? []).filter(id => id !== snapshot.projectId) } : {}),
    ...(snapshot.projectCompleted && data.planner ? { planner: { ...data.planner,
      projectIds: data.planner.projectIds.filter(id => id !== snapshot.projectId) } } : {}),
    projects: snapshot.project
      ? data.projects.some(row => row.id === snapshot.projectId)
        ? data.projects.map(row => row.id === snapshot.projectId ? snapshot.project! : row)
        : [...data.projects, snapshot.project]
      : data.projects.filter(row => row.id !== snapshot.projectId),
    issuances: replaceProjectRows(data.issuances, snapshot.issuances),
    workReservations: replaceProjectRows(data.workReservations, snapshot.workReservations),
    inspections: [...data.inspections.filter(row => !oldReturnIds.has(row.returnId)), ...snapshot.inspections],
  };
};

/** One server baseline, reversible overlays, and a read fence shared by every page. */
export class WorkDataStore {
  private baseline: BootstrapResponse | null = null;
  private overlays = new Map<string, { entry: PendingWorkMutation; patch?: WorkDataPatch; retry: () => Promise<unknown> }>();
  private listeners = new Set<() => void>();
  private epoch = 0;
  private session = 0;
  private reads = 0;
  private acceptedRead = 0;
  private recordVersions = new Map<string, number>();
  private snapshot: { data: BootstrapResponse | null; pending: PendingWorkMutation[]; version: number; recordVersions: ReadonlyMap<string, number> } = { data: null, pending: [], version: 0, recordVersions: new Map() };
  private flights = new Map<string, Promise<unknown>>();
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getSnapshot = () => this.snapshot;
  readToken = () => ({ epoch: this.epoch, session: this.session, sequence: ++this.reads });
  receive = (data: BootstrapResponse, token: ReturnType<WorkDataStore["readToken"]>) => {
    if (token.session !== this.session || token.epoch !== this.epoch || token.sequence < this.acceptedRead) return false;
    this.acceptedRead = token.sequence;
    this.baseline = data;
    this.publish();
    return true;
  };
  clear = () => {
    this.session++; this.epoch++; this.baseline = null; this.overlays.clear(); this.flights.clear(); this.recordVersions.clear(); this.publish();
  };
  private touchRecords(ids: string[]) { for (const id of ids) this.recordVersions.set(id, this.snapshot.version + 1); }
  private publish() {
    let data = this.baseline;
    if (data) for (const { patch } of this.overlays.values()) if (patch) data = patch(data);
    this.snapshot = { data, pending: [...this.overlays.values()].map(value => value.entry), version: this.snapshot.version + 1, recordVersions: new Map(this.recordVersions) };
    this.listeners.forEach(listener => listener());
  }
  retry = (key: string) => this.overlays.get(key)?.retry();

  execute<T extends WorkMutationResult>(mutation: WorkMutation<T>, refresh: () => Promise<void>): Promise<T> {
    const existing = this.flights.get(mutation.key);
    if (existing) return existing as Promise<T>;
    // An ambiguous response must be resolved using its original key before any
    // new operation (including a second click from another page) is submitted.
    if ([...this.overlays.values()].some(value => value.entry.key !== mutation.key) || [...this.flights.keys()].some(key => key !== mutation.key))
      return Promise.reject(new Error("저장 중인 변경을 먼저 확인해 주세요."));
    const session = this.session;
    const run = async () => {
      if (session !== this.session) throw new Error("로그인 상태가 변경되어 요청을 취소했습니다.");
      this.overlays.set(mutation.key, { entry: { key: mutation.key, label: mutation.label, entityIds: mutation.entityIds, status: "saving" },
        patch: mutation.optimistic, retry: () => this.execute(mutation, refresh) });
      this.touchRecords(mutation.entityIds);
      this.publish();
      try {
        const result = await mutation.request();
        if (session !== this.session) return result;
        if (result.snapshot && this.baseline) {
          this.touchRecords([...this.baseline.issuances.filter(row => row.projectId === result.snapshot!.projectId).map(row => row.id), ...result.snapshot.issuances.map(row => row.id)]);
          this.baseline = mergeWorkSnapshot(this.baseline, result.snapshot);
          this.overlays.delete(mutation.key);
        } else {
          // Compatibility with an older response: retain accepted values in the
          // baseline until a fresh read arrives. Never repeat an accepted write.
          if (this.baseline && mutation.optimistic) this.baseline = mutation.optimistic(this.baseline);
          this.overlays.delete(mutation.key);
        }
        if (result.planItem && this.baseline) {
          const item = result.planItem;
          this.baseline = { ...this.baseline, planRuns: this.baseline.planRuns.map(run => run.id === item.planRunId
            ? { ...run, items: run.items.map(previous => previous.id === item.id ? item : previous) } : run) };
        }
        this.epoch++;
        this.publish();
        // UI callbacks cannot change a successfully committed operation into an
        // ambiguous one. Refresh remains independently retryable.
        try { mutation.onSuccess?.(result); } catch { /* Preserve the confirmed result. */ }
        // Read errors are independent of the already-committed write.
        void refresh().catch(() => {});
        return result;
      } catch (cause) {
        if (session !== this.session) throw cause;
        if (cause instanceof ApiError && cause.status >= 400 && cause.status < 500) {
          this.overlays.delete(mutation.key);
          this.publish();
          if (cause.status === 409 || cause.status === 428) void refresh().catch(() => {});
          throw cause;
        }
        const overlay = this.overlays.get(mutation.key);
        if (overlay) overlay.entry = { ...overlay.entry, status: "uncertain" };
        this.publish();
        throw new Error("저장 결과를 확인하지 못했습니다. ‘저장 결과 확인’으로 확인해 주세요.", { cause });
      }
    };
    // Start in a microtask so a synchronous double click sees the same flight.
    const flight = Promise.resolve().then(run).finally(() => {
      if (this.flights.get(mutation.key) === flight) this.flights.delete(mutation.key);
    });
    this.flights.set(mutation.key, flight);
    return flight;
  }
}
